import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  AfterViewInit,
  Output,
  Renderer2,
  SimpleChanges,
} from '@angular/core'
import { NgFor } from '@angular/common'
import { ProjectService } from '../../../services/project.service'
import {
  MenuType,
  ProjectCMSMenu,
  ProjectCustomizationGlobalConfig,
  ProjectMenuCustomization,
  ScreenResolution,
} from 'htv-lib'
import { Subscription } from 'rxjs'

@Component({
  selector: 'app-nav-bar',
  standalone: true,
  imports: [NgFor],
  templateUrl: './nav-bar.component.html',
  styleUrls: ['./nav-bar.component.scss'],
})
export class NavBarComponent implements OnInit, OnChanges, OnDestroy, AfterViewInit {
  private uid = `mc-${Math.random().toString(36).slice(2, 8)}`
  private styleEl!: HTMLStyleElement
  private rules = new Map<string, Record<string, string | number>>()

  @Input() menus: ProjectCMSMenu[] = []
  @Input() resolution: ScreenResolution = ScreenResolution.RES_1080P
  @Output() buttonClicked = new EventEmitter<ProjectCMSMenu>()
  selectedIndex = 0

  resolutionMultiplier = 1.0

  projectLoadedSubscription?: Subscription

  constructor(
    private projectService: ProjectService,
    private renderer: Renderer2,
    private el: ElementRef
  ) {
    // do nothing
  }

  ngOnInit(): void {
    this.el.nativeElement.setAttribute('data-uid', this.uid)
    this.initStyleEl()

    this.applyResolutionStyles()

    this.loadGeneralTheme()
    this.loadActiveButtonTheme()
    this.loadInactiveButtonTheme()
    this.renderRules()
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.initStyleEl()

    if (changes['menus']) {
      this.selectDefaultMenu()
    }
    if (changes['resolution']) {
      this.applyResolutionStyles()

      this.loadGeneralTheme()
      this.loadActiveButtonTheme()
      this.loadInactiveButtonTheme()
      this.renderRules()
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      const config = this.projectService.getProjectGlobalCustomization()
      if (config) {
        this.applyDirectPositionStyles(config)
      }
    }, 100)
  }

  ngOnDestroy(): void {
    this.projectLoadedSubscription?.unsubscribe()
  }

  initStyleEl(): void {
    if (this.styleEl) return
    this.styleEl = document.createElement('style')
    this.styleEl.setAttribute('uid', this.uid)
    this.el.nativeElement.appendChild(this.styleEl)
  }

  selectDefaultMenu(): void {
    const defaultMenuIndex = this.menus.findIndex(menu => menu.default)
    if (defaultMenuIndex !== -1) {
      this.selectedIndex = defaultMenuIndex
    }
  }

  onClick(navButton: ProjectCMSMenu): void {
    this.buttonClicked.emit(navButton)
  }

  applyResolutionStyles(): void {
    switch (this.resolution) {
      case ScreenResolution.RES_720P:
        this.resolutionMultiplier = 0.67
        break

      case ScreenResolution.RES_1080P:
        this.resolutionMultiplier = 1.0
        break

      case ScreenResolution.RES_4K:
        this.resolutionMultiplier = 2.0
        break
    }
  }

  loadGeneralTheme() {
    const config = this.projectService.getProjectGlobalCustomization()
    if (config) {
      this.applyGlobalStyles(config)
      this.applyDirectPositionStyles(config)
    }
  }

  loadActiveButtonTheme() {
    const config = this.projectService.getProjectActiveButtonCustomization()
    if (config) {
      this.applyMenuStyles(config, true)
    }
  }

  loadInactiveButtonTheme() {
    const config = this.projectService.getProjectInactiveButtonCustomization()
    if (config) {
      this.applyMenuStyles(config, false)
    }
  }

  applyDirectPositionStyles(config: ProjectCustomizationGlobalConfig) {
    if (!this.el || !this.el.nativeElement) return

    const navBar = this.el.nativeElement.querySelector('.nav-bar')
    const scrollWrapper = this.el.nativeElement.querySelector('.nav-bar__scroll-wrapper')
    const container = this.el.nativeElement.querySelector('.nav-bar__container')

    const generalConfig = this.projectService.getProjectGeneralConfig()
    const isVertical = generalConfig?.menuOrientation === 'vertical'

    if (config.menuPosition) {
      const scaledX = this.applyMultiplier(config.menuPosition.x)
      const scaledY = this.applyMultiplier(config.menuPosition.y)

      navBar.style.setProperty('top', `${scaledY}px`)
      navBar.style.setProperty('left', `${scaledX}px`)

      if (isVertical) {
        const maxHeight = config.menuContainerSize
          ? this.applyMultiplier(config.menuContainerSize)
          : window.innerHeight - scaledY
        scrollWrapper.style.setProperty('max-height', `${maxHeight}px`)
        scrollWrapper.style.setProperty('overflow-y', 'auto')
        scrollWrapper.style.setProperty('overflow-x', 'visible')
      } else {
        const maxWidth = config.menuContainerSize
          ? this.applyMultiplier(config.menuContainerSize)
          : window.innerWidth - scaledX

        scrollWrapper.style.setProperty('max-width', `${maxWidth}px`)
        scrollWrapper.style.setProperty('overflow-x', 'auto')
        scrollWrapper.style.setProperty('overflow-y', 'visible')
      }
    }

    if (generalConfig?.menuOrientation) {
      if (isVertical) {
        this.renderer.setAttribute(navBar, 'menu-vertical', 'true')
      } else {
        this.renderer.removeAttribute(navBar, 'menu-vertical')
      }

      this.renderer.setAttribute(container, 'menu-orientation', generalConfig.menuOrientation)
    }
  }

  applyGlobalStyles(config: ProjectCustomizationGlobalConfig) {
    const generalConfig = this.projectService.getProjectGeneralConfig()

    // .nav-bar
    const navBarStyles: Record<string, string | number> = {}
    if (config.menuPosition) {
      navBarStyles['top'] = `${this.applyMultiplier(config.menuPosition.y)}px`
      navBarStyles['left'] = `${this.applyMultiplier(config.menuPosition.x)}px`
    }
    this.setRule('.nav-bar', navBarStyles)

    // .nav-bar[menu-vertical="true"]
    const navBarVerticalStyles: Record<string, string | number> = {}
    if (config.menuSize) {
      navBarVerticalStyles['width'] = `${this.applyMultiplier(config.menuSize.width)}px`
    }
    this.setRule('.nav-bar[menu-vertical="true"]', navBarVerticalStyles)

    // .nav-button
    const navButtonStyles: Record<string, string | number> = {}
    if (config.menuSize) {
      navButtonStyles['width'] = `${this.applyMultiplier(config.menuSize.width)}px`
      navButtonStyles['height'] = `${this.applyMultiplier(config.menuSize.height)}px`
    }
    navButtonStyles['margin-right'] = `${this.applyMultiplier(config.menuMargin)}px`

    if (generalConfig?.showMenuBackground !== undefined) {
      const backgroundColor = generalConfig.showMenuBackground
        ? generalConfig.menuBackgroundColor || '#FFFFFF'
        : 'transparent'
      navButtonStyles['background-color'] = backgroundColor
    }
    this.setRule('.nav-button', navButtonStyles)

    // .nav-bar[menu-vertical="true"] .nav-bar__container .nav-button
    const navButtonVerticalStyles: Record<string, string | number> = {}
    navButtonVerticalStyles['margin-bottom'] = `${this.applyMultiplier(config.menuMargin)}px`
    this.setRule('.nav-bar[menu-vertical="true"] .nav-bar__container .nav-button', navButtonVerticalStyles)

    // .nav-button.htv-selected
    const navButtonSelectedStyles: Record<string, string | number> = {}
    if (generalConfig?.showMenuBackground !== undefined) {
      const backgroundColor = generalConfig.showMenuBackground
        ? generalConfig.menuBackgroundColor || '#FFFFFF'
        : 'transparent'
      navButtonSelectedStyles['background-color'] = backgroundColor
    }
    this.setRule('.nav-button.htv-selected', navButtonSelectedStyles)

    // .nav-button__icon
    const navButtonIconStyles: Record<string, string | number> = {}
    if (config.imagePosition) {
      navButtonIconStyles['top'] = `${this.applyMultiplier(config.imagePosition.y)}px`
      navButtonIconStyles['left'] = `${this.applyMultiplier(config.imagePosition.x)}px`
      navButtonIconStyles['width'] = `${this.applyMultiplier(config.imagePosition.width)}px`
      navButtonIconStyles['height'] = `${this.applyMultiplier(config.imagePosition.height)}px`
    }
    if (generalConfig?.menuType) {
      const showImage =
        generalConfig.menuType === MenuType.IMAGE_ONLY || generalConfig.menuType === MenuType.TEXT_AND_IMAGE
      navButtonIconStyles['display'] = showImage ? 'block' : 'none'
    }
    this.setRule('.nav-button__icon', navButtonIconStyles)

    // .nav-button__label
    const navButtonLabelStyles: Record<string, string | number> = {}
    if (config.textPosition) {
      navButtonLabelStyles['top'] = `${this.applyMultiplier(config.textPosition.y)}px`
      navButtonLabelStyles['left'] = `${this.applyMultiplier(config.textPosition.x)}px`
      navButtonLabelStyles['width'] = `${this.applyMultiplier(config.textPosition.width)}px`
    }
    if (generalConfig?.menuType) {
      const showText =
        generalConfig.menuType === MenuType.TEXT_ONLY || generalConfig.menuType === MenuType.TEXT_AND_IMAGE
      navButtonLabelStyles['display'] = showText ? 'flex' : 'none'
    }
    this.setRule('.nav-button__label', navButtonLabelStyles)

    this.renderRules()
  }

  applyMenuStyles(config: ProjectMenuCustomization, activeMenu: boolean) {
    // .nav-button
    const navButtonSelector = activeMenu ? '.nav-button.htv-selected' : '.nav-button'
    const navButtonStyles: Record<string, string | number> = {
      'border-radius': `${this.applyMultiplier(config.cornerRadius)}px`,
      opacity: config.opacity,
    }
    if (config.shadow) {
      if (config.shadow.shadowEnabled) {
        const offsetX = this.applyMultiplier(config.shadow.shadowOffsetX)
        const offsetY = this.applyMultiplier(config.shadow.shadowOffsetY)
        const blur = this.applyMultiplier(config.shadow.shadowBlur)
        const color = config.shadow.shadowColor || 'rgba(0, 0, 0, 0.5)'
        navButtonStyles['box-shadow'] = `${offsetX}px ${offsetY}px ${blur}px ${color}`
      } else {
        navButtonStyles['box-shadow'] = 'none'
      }
    }
    this.setRule(navButtonSelector, navButtonStyles)

    // .nav-button__label
    if (config.text) {
      const navButtonLabelSelector = activeMenu ? '.nav-button.htv-selected .nav-button__label' : '.nav-button__label'
      const navButtonLabelStyles: Record<string, string | number> = {}
      navButtonLabelStyles['font-size'] = `${this.applyMultiplier(config.text.fontSize)}px`
      navButtonLabelStyles['font-weight'] = config.text.fontWeight || 'normal'
      navButtonLabelStyles['font-style'] = config.text.fontStyle || 'normal'
      navButtonLabelStyles['text-decoration'] = config.text.textDecoration || 'none'
      navButtonLabelStyles['color'] = config.text.color || '#000'
      navButtonLabelStyles['text-align'] = config.text.textAlign || 'center'
      navButtonLabelStyles['justify-content'] = config.text.textAlign || 'center'
      this.setRule(navButtonLabelSelector, navButtonLabelStyles)
    }

    // .nav-button__label::after
    const navButtonAfterSelector = activeMenu ? '.nav-button.htv-selected::after' : '.nav-button::after'
    if (config.underline) {
      const navButtonAfterStyles: Record<string, string | number> = {}
      if (config.underline.underlineMenu) {
        navButtonAfterStyles['opacity'] = '1'
        navButtonAfterStyles['background-color'] = config.underline.underlineColor || '#000000'
        navButtonAfterStyles['height'] = `${this.applyMultiplier(config.underline.underlineThickness)}px`
        navButtonAfterStyles['width'] = `${config.underline.underlineWidth || 100}%`
      } else {
        navButtonAfterStyles['opacity'] = '0'
      }
      this.setRule(navButtonAfterSelector, navButtonAfterStyles)
    } else {
      this.setRule(navButtonAfterSelector, { opacity: '0' })
    }
  }

  applyMultiplier(value: number): number {
    return Math.round(value * this.resolutionMultiplier)
  }

  setRule(selector: string, styles: Record<string, string | number>): void {
    const existingRules = this.rules.get(selector) ?? {}
    this.rules.set(selector, { ...existingRules, ...styles })
  }

  renderRules(): void {
    this.styleEl.textContent = Array.from(this.rules.entries())
      .map(([selector, styles]) => {
        const props = Object.entries(styles)
          .map(([prop, val]) => `${prop}: ${val} !important`)
          .join('; ')
        return `[data-uid="${this.uid}"] ${selector} { ${props} }`
      })
      .join('\n')
  }
}
